import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiCrop, FiRefreshCw, FiRotateCw, FiX, FiZoomIn, FiZoomOut } from 'react-icons/fi';
import { useLanguage } from '../../i18n';
import { BLUE } from './brand';

/** Aspect presets for stories / posts (label → ratio → export size). */
export const CROP_RATIOS = [
  { key: 'story', label: '9:16', value: 9 / 16, out: [720, 1280] },
  { key: 'post', label: '4:5', value: 4 / 5, out: [1024, 1280] },
  { key: 'square', label: '1:1', value: 1, out: [1080, 1080] },
  { key: 'wide', label: '16:9', value: 16 / 9, out: [1080, 608] },
];

/**
 * Story image editor: **crop / format** with drag, a zoom slider, 90° rotation
 * and a ratio picker (9:16 story, 4:5, 1:1, 16:9). `onDone(file, info)` gets a
 * JPEG ready to upload — exactly what the frame shows.
 *
 * GIFs are skipped by the editor (a canvas export would freeze the animation).
 */
export default function ImageCropper({ src, ratio: initialRatio = 'story', onDone, onCancel }) {
  const { t } = useLanguage();
  const [ratioKey, setRatioKey] = useState(initialRatio);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [current, setCurrent] = useState(src);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [busy, setBusy] = useState(false);
  const drag = useRef(null);
  const img = useRef(null);

  const ratio = useMemo(
    () => CROP_RATIOS.find((r) => r.key === ratioKey) || CROP_RATIOS[0],
    [ratioKey],
  );

  // Stage geometry (fixed height, the width follows the chosen format).
  const stageH = 380;
  const stageW = Math.round(stageH * ratio.value);

  useEffect(() => {
    setCurrent(src);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, [src]);

  const baseScale = natural.w && natural.h
    ? Math.max(stageW / natural.w, stageH / natural.h)
    : 1;
  const dw = natural.w * baseScale * zoom;
  const dh = natural.h * baseScale * zoom;
  const left = stageW / 2 + offset.x - dw / 2;
  const top = stageH / 2 + offset.y - dh / 2;

  const onPointerDown = (e) => {
    drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    setOffset({
      x: drag.current.ox + (e.clientX - drag.current.x),
      y: drag.current.oy + (e.clientY - drag.current.y),
    });
  };
  const onPointerUp = () => { drag.current = null; };

  /** Rotate the source bitmap 90° (baked in, so the position maths stays simple). */
  const rotate90 = () => {
    const im = img.current;
    if (!im || !im.naturalWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = im.naturalHeight;
    canvas.height = im.naturalWidth;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(im, -im.naturalWidth / 2, -im.naturalHeight / 2);
    setCurrent(canvas.toDataURL('image/jpeg', 0.95));
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  /** Cut out exactly what the frame shows and hand back a JPEG File. */
  const apply = () => {
    const im = img.current;
    if (!im || !natural.w || !dw) return;
    setBusy(true);
    try {
      const [outW, outH] = ratio.out;
      const s = outW / stageW;
      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, outW, outH);
      ctx.drawImage(im, left * s, top * s, dw * s, dh * s);
      canvas.toBlob((blob) => {
        if (!blob) { setBusy(false); return; }
        const file = new File([blob], `crop-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setBusy(false);
        if (onDone) onDone(file, { width: outW, height: outH, ratio: ratio.label });
      }, 'image/jpeg', 0.92);
    } catch (e) {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800/60">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-600 dark:text-gray-300">
          <FiCrop className="w-4 h-4" /> {t('cropFormat')} · {ratio.label}
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="p-1 rounded-lg text-gray-500 hover:bg-gray-200/60 dark:hover:bg-gray-700"
          aria-label={t('cancel')}
        >
          <FiX className="w-4 h-4" />
        </button>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-3 p-3">
        {/* stage — drag to move, slider to zoom */}
        <div
          className="crop-stage rounded-xl shrink-0"
          style={{ width: stageW, height: stageH, maxWidth: '100%' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <img
            ref={img}
            src={current}
            alt="crop"
            draggable={false}
            onLoad={(e) => setNatural({
              w: e.target.naturalWidth || 0,
              h: e.target.naturalHeight || 0,
            })}
            className="absolute select-none"
            style={{ width: dw || undefined, height: dh || undefined, left, top }}
          />
          <span className="crop-mask rounded-xl" />
          <span className="absolute inset-0 pointer-events-none border-2 border-white/70 rounded-xl" />
        </div>

        {/* controls */}
        <div className="w-full sm:w-52 space-y-3">
          <div>
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-1">{t('format')}</p>
            <div className="flex flex-wrap gap-1.5">
              {CROP_RATIOS.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => { setRatioKey(r.key); setOffset({ x: 0, y: 0 }); }}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border ${
                    ratioKey === r.key
                      ? 'text-white'
                      : 'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                  }`}
                  style={ratioKey === r.key ? { background: BLUE, borderColor: BLUE } : undefined}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-1">{t('zoom')}</p>
            <div className="flex items-center gap-2">
              <FiZoomOut className="w-4 h-4 text-gray-400" />
              <input
                type="range"
                min="1"
                max="3"
                step="0.02"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1"
              />
              <FiZoomIn className="w-4 h-4 text-gray-400" />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={rotate90}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-[11px] font-bold text-gray-600 dark:text-gray-300"
            >
              <FiRotateCw className="w-3.5 h-3.5" /> {t('rotate')}
            </button>
            <button
              type="button"
              onClick={() => { setZoom(1); setOffset({ x: 0, y: 0 }); }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-[11px] font-bold text-gray-600 dark:text-gray-300"
            >
              <FiRefreshCw className="w-3.5 h-3.5" /> {t('resetCrop')}
            </button>
          </div>

          <p className="text-[10px] text-gray-400">{t('cropHint')}</p>

          <button
            type="button"
            onClick={apply}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-60"
            style={{ background: BLUE }}
          >
            <FiCrop className="w-4 h-4" /> {busy ? t('uploading') : t('applyCrop')}
          </button>
        </div>
      </div>
    </div>
  );
}

